import { useState, useEffect } from 'react';
import { Community, CommunityRedirectInfo } from 'redux/types/account';
import { fetchCommunityRedirectInfo } from 'services/api/community';

export const shouldRedirectToNewFrontend = (path: string): boolean => {
  return (path.includes('admin-panel') || path.includes('application')) && !path.includes('events-settings');
};

export const getStagingUrl = () => {
  if (window.location.href.includes('app-old-preview-1.babele.co')) {
    return 'https://app-preview-1.babele.co';
  }

  if (window.location.href.includes('app-old-preview-2.babele.co')) {
    return 'https://app-preview-2.babele.co';
  }

  if (window.location.href.includes('app-old-preview-3.babele.co')) {
    return 'https://app-preview-3.babele.co';
  }

  return 'https://app-new-stag.babele.co';
};

const getProductionRedirectUrl = (redirectInfo: CommunityRedirectInfo, path: string): string => {
  const { customDnsHost, customDnsNewFrontendHost } = redirectInfo;

  // If community has a new frontend host configured, always prefer it
  if (customDnsNewFrontendHost) {
    return `https://${customDnsNewFrontendHost}${path}`;
  }

  // Handle app-ms.babele.co
  if (window.origin.includes('app-ms.babele.co')) {
    return `https://app-new-ms.babele.co${path}`;
  }

  // Handle main babele.co domain
  if (window.origin.includes('app.babele.co')) {
    return `https://app-new.babele.co${path}`;
  }

  // For custom domains without newFrontendHost configured
  if (customDnsHost) {
    // Try to derive new frontend URL from custom DNS pattern
    if (customDnsHost.includes('app.')) {
      const newHost = customDnsHost.replace('app.', 'app-new.');
      return `https://${newHost}${path}`;
    }
    // If custom DNS doesn't follow app. pattern, fall back to babele.co
    console.warn(`Custom DNS ${customDnsHost} does not follow app. pattern, redirecting to app-new.babele.co`);
    return `https://app-new.babele.co${path}`;
  }

  // Default fallback: try to transform current host
  const currentHost = window.location.host;
  if (currentHost.includes('app.')) {
    const newHost = currentHost.replace('app.', 'app-new.');
    return `https://${newHost}${path}`;
  }

  // Ultimate fallback
  return `https://app-new.babele.co${path}`;
};

export const getNewFrontendRedirectUrl = (redirectInfo: CommunityRedirectInfo, path: string): string | null => {
  if (!redirectInfo.isNewFrontendEnabled) {
    return null;
  }

  if (!shouldRedirectToNewFrontend(path)) {
    return null;
  }

  // Determine environment
  let env: 'local' | 'staging' | 'production' = 'local';
  if (process.env.REACT_APP_DEPLOYMENT_TYPE === 'staging' && !window.origin.includes('localhost')) {
    env = 'staging';
  }
  if (process.env.REACT_APP_DEPLOYMENT_TYPE === 'production') {
    env = 'production';
  }

  // Build redirect URL based on environment
  if (env === 'local') {
    return `http://localhost:3001${path}`;
  }

  if (env === 'staging') {
    return `${getStagingUrl()}${path}`;
  }

  // Production logic with improved custom DNS handling
  return getProductionRedirectUrl(redirectInfo, path);
};

export const useNewFrontendRedirect = (communityId: number | undefined) => {
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    if (!communityId) {
      setIsChecking(false);
      return;
    }

    const path = window.location.pathname;
    if (!shouldRedirectToNewFrontend(path)) {
      setIsChecking(false);
      return;
    }

    fetchCommunityRedirectInfo(communityId)
      .then(redirectInfo => {
        const redirectUrl = getNewFrontendRedirectUrl(redirectInfo, path);
        if (redirectUrl) {
          window.location.href = redirectUrl;
          // Keep isChecking true to prevent rendering while redirecting
        } else {
          setIsChecking(false);
        }
      })
      .catch(error => {
        console.error('Failed to fetch redirect info:', error);
        setIsChecking(false);
      });
  }, [communityId]);

  return { isCheckingRedirect: isChecking };
};

export const handleRedirect = (community: Community) => {
  // Convert Community to CommunityRedirectInfo format
  const redirectInfo: CommunityRedirectInfo = {
    customDnsHost: community.customDns?.host ?? null,
    customDnsNewFrontendHost: community.customDns?.newFrontendHost ?? null,
    isNewFrontendEnabled: community.isNewFrontendEnabled ?? null,
  };

  const redirectUrl = getNewFrontendRedirectUrl(redirectInfo, window.location.pathname);
  if (redirectUrl) {
    window.location.href = redirectUrl;
  }
};

export const getNewFrontendHost = (community: Community) => {
  if (process.env.REACT_APP_DEPLOYMENT_TYPE === 'production') {
    if (community?.customDns?.newFrontendHost) {
      return `https://${community.customDns.newFrontendHost}`;
    }

    if (window.origin.includes('app-ms.babele.co')) {
      return 'https://app-new-ms.babele.co';
    }

    return 'https://app-new.babele.co';
  }

  if (process.env.REACT_APP_DEPLOYMENT_TYPE === 'staging') {
    return getStagingUrl();
  }

  return 'http://localhost:3001';
};
